import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getClients, createClient, updateClient, deleteClient, getOperationalCases, createOperationalCase } from '../lib/api';
import { formatDate, statusLabels, getStatusClass, movementTypeLabels, plans, clientOrigins, getTodayDate } from '../lib/utils';
import { useAllPlans } from '../lib/usePlans';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Search, Users, Edit, History, Loader2, Phone, Mail, FileText, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const Clientes = () => {
  const { isDirection } = useAuth();
  const allPlans = useAllPlans();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [origenFilter, setOrigenFilter] = useState('');
  const [showNewDialog, setShowNewDialog] = useState(searchParams.get('action') === 'new');
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showCaseDialog, setShowCaseDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientCases, setClientCases] = useState([]);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', rut: '', current_plan: '',
    status: 'activo', origen: 'manual', observations: '',
    fecha_inicio_plan: '', fecha_fin_plan: '',
    telefono_emergencia: '', lesiones: ''
  });
  const [caseData, setCaseData] = useState({
    tipo_accion: '', plan: '', origen: 'manual', observaciones: '',
    fecha_accion: getTodayDate()
  });
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;

  useEffect(() => { fetchClients(); }, [statusFilter, origenFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClients = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (origenFilter) params.origen = origenFilter;
      if (search) params.search = search;
      const response = await getClients(params);
      setClients(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally { setLoading(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchClients();
  };

  const handleNewClient = () => {
    setFormData({ name: '', email: '', phone: '', rut: '', current_plan: '', status: 'activo', origen: 'manual', observations: '', fecha_inicio_plan: '', fecha_fin_plan: '', telefono_emergencia: '', lesiones: '' });
    setSelectedClient(null);
    setShowNewDialog(true);
  };

  const handleEditClient = (client) => {
    setFormData({
      name: client.name, email: client.email || '', phone: client.phone || '',
      rut: client.rut || '', current_plan: client.current_plan || '',
      status: client.status, origen: client.origen || 'manual',
      observations: client.observations || '',
      fecha_inicio_plan: client.fecha_inicio_plan || '',
      fecha_fin_plan: client.fecha_fin_plan || '',
      telefono_emergencia: client.telefono_emergencia || '',
      lesiones: Array.isArray(client.lesiones) ? client.lesiones.join('\n') : (client.lesiones || '')
    });
    setSelectedClient(client);
    setShowNewDialog(true);
  };

  const handleDeleteClient = async (client) => {
    if (!window.confirm(`Eliminar cliente "${client.name}"? Esta accion no se puede deshacer.`)) return;
    try {
      await deleteClient(client.id);
      toast.success('Cliente eliminado');
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleViewClient = async (client) => {
    setSelectedClient(client);
    try {
      const casesRes = await getOperationalCases({ search: client.name });
      setClientCases(casesRes.data);
    } catch (error) {
      
    }
    setShowDetailDialog(true);
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        lesiones: formData.lesiones ? formData.lesiones.split('\n').map(l => l.trim()).filter(Boolean) : []
      };
      if (selectedClient) {
        await updateClient(selectedClient.id, payload);
        toast.success('Cliente actualizado');
      } else {
        await createClient(payload);
        toast.success('Cliente creado');
      }
      setShowNewDialog(false);
      fetchClients();
    } catch (error) {
      toast.error('Error al guardar cliente');
    } finally { setSaving(false); }
  };

  const handleNewCase = (client) => {
    setSelectedClient(client);
    setCaseData({
      tipo_accion: '', plan: client.current_plan || '', origen: client.origen || 'manual',
      observaciones: '', fecha_accion: new Date().toISOString().split('T')[0]
    });
    setShowCaseDialog(true);
  };

  const handleSaveCase = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createOperationalCase({
        client_id: selectedClient.id,
        client_name: selectedClient.name,
        ...caseData
      });
      toast.success('Caso operativo registrado');
      setShowCaseDialog(false);
      fetchClients();
    } catch (error) {
      toast.error('Error al registrar caso');
    } finally { setSaving(false); }
  };

  const filteredClients = useMemo(() => clients.filter(client => {
    const matchSearch = !search || client.name.toLowerCase().includes(search.toLowerCase()) ||
      (client.email && client.email.toLowerCase().includes(search.toLowerCase())) ||
      (client.rut && client.rut.includes(search));
    const matchStatus = !statusFilter || client.status === statusFilter;
    const matchOrigen = !origenFilter || client.origen === origenFilter;
    return matchSearch && matchStatus && matchOrigen;
  }), [clients, search, statusFilter, origenFilter]);

  const totalPages = Math.ceil(filteredClients.length / ITEMS_PER_PAGE);
  const paginatedClients = useMemo(() => filteredClients.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE), [filteredClients, currentPage]);

  useEffect(() => { setCurrentPage(1); }, [search, statusFilter, origenFilter]);

  const getOrigenBadge = (origen) => {
    const colors = {
      virtualpos: 'bg-blue-100 text-blue-700',
      shopify: 'bg-purple-100 text-purple-700',
      manual: 'bg-gray-100 text-gray-600',
      pos_presencial: 'bg-emerald-100 text-emerald-700',
      transferencia: 'bg-amber-100 text-amber-700',
    };
    const labels = { virtualpos: 'VPos', shopify: 'Shopify', manual: 'Manual', pos_presencial: 'POS', transferencia: 'Transfer.' };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${colors[origen] || 'bg-gray-100 text-gray-600'}`}>
        {labels[origen] || origen || '-'}
      </span>
    );
  };

  const getAntiguedad = (client) => {
    const ref = client.fecha_inicio_plan || client.created_at?.slice(0, 10);
    if (!ref) return '-';
    const start = new Date(ref + (ref.length === 10 ? 'T00:00:00' : ''));
    const now = new Date();
    const diffMs = now - start;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days < 30) return `${days}d`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}m`;
    const years = Math.floor(months / 12);
    const rem = months % 12;
    return rem > 0 ? `${years}a ${rem}m` : `${years}a`;
  };

  const getPlanExpiryStatus = (client) => {
    if (!client.fecha_fin_plan || client.status !== 'activo') return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fin = new Date(client.fecha_fin_plan + 'T00:00:00');
    const diff = Math.ceil((fin - today) / (1000 * 60 * 60 * 24));
    if (diff < 0) return { label: 'Vencido', class: 'bg-[#FBEAE9] text-[#C25953]', urgent: true };
    if (diff <= 7) return { label: `Vence en ${diff}d`, class: 'bg-[#FFF3E0] text-[#E65100]', urgent: true };
    if (diff <= 15) return { label: `Vence en ${diff}d`, class: 'bg-[#FFF8E1] text-[#F57F17]', urgent: false };
    return null;
  };

  const expiringClients = filteredClients.filter(c => getPlanExpiryStatus(c)?.urgent);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="clients-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Clientes</h1>
          <p className="page-subtitle">Gestión de clientes del estudio ({filteredClients.length})</p>
        </div>
        <Button className="btn-primary gap-2" onClick={handleNewClient} data-testid="btn-new-client">
          <Plus size={18} /> Nuevo Cliente
        </Button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-3">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C7083]" size={16} />
              <Input placeholder="Buscar por nombre, email o RUT..." value={search}
                onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="input-search-clients" />
            </div>
          </form>
          <Select value={statusFilter || "all"} onValueChange={(v) => {
            const val = v === "all" ? "" : v;
            setStatusFilter(val);
            setSearchParams(val ? { status: val } : {});
          }}>
            <SelectTrigger className="w-[160px]" data-testid="select-status-filter">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="activo">Activo</SelectItem>
              <SelectItem value="congelado">Congelado</SelectItem>
              <SelectItem value="cancelado">Cancelado</SelectItem>
            </SelectContent>
          </Select>
          <Select value={origenFilter || "all"} onValueChange={(v) => setOrigenFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px]" data-testid="select-origen-filter">
              <SelectValue placeholder="Origen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {clientOrigins.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Expiring Plans Alert */}
      {expiringClients.length > 0 && (
        <div className="relative overflow-hidden rounded-xl border-2 border-[#E65100] bg-gradient-to-r from-[#FFF3E0] to-[#FFF8E1] p-4" data-testid="expiring-plans-alert">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#E65100] rounded-lg flex-shrink-0">
              <FileText size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-[#1A2835]">Planes por vencer ({expiringClients.length})</p>
              <p className="text-sm text-[#5C7083]">
                {expiringClients.slice(0, 3).map(c => c.name).join(', ')}
                {expiringClients.length > 3 ? ` y ${expiringClients.length - 3} más` : ''}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Clients Table */}
      <div className="card p-0 overflow-hidden" data-testid="clients-table">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Contacto</th>
                <th>Plan</th>
                <th>Inicio Plan</th>
                <th>Fin Plan</th>
                <th>Origen</th>
                <th>Antig.</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12">
                    <Users className="mx-auto h-12 w-12 text-[#E8E2D7] mb-4" />
                    <p className="text-[#5C7083]">No se encontraron clientes</p>
                  </td>
                </tr>
              ) : (
                paginatedClients.map((client) => {
                  const expiry = getPlanExpiryStatus(client);
                  return (
                  <tr key={client.id} data-testid={`client-row-${client.id}`}>
                    <td>
                      <div>
                        <p className="font-medium text-[#1A2835]">{client.name}</p>
                        {client.rut && <p className="text-xs text-[#5C7083]">{client.rut}</p>}
                      </div>
                    </td>
                    <td>
                      <div className="space-y-0.5">
                        {client.email && <p className="text-sm flex items-center gap-1"><Mail size={12} className="text-[#5C7083]" />{client.email}</p>}
                        {client.phone && <p className="text-sm flex items-center gap-1"><Phone size={12} className="text-[#5C7083]" />{client.phone}</p>}
                      </div>
                    </td>
                    <td className="text-sm">{client.current_plan || '-'}</td>
                    <td className="text-sm text-[#5C7083]">{client.fecha_inicio_plan || '-'}</td>
                    <td className="text-sm">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[#5C7083]">{client.fecha_fin_plan || '-'}</span>
                        {expiry && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${expiry.class}`} data-testid={`expiry-badge-${client.id}`}>
                            {expiry.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{getOrigenBadge(client.origen)}</td>
                    <td className="text-xs text-[#5C7083]" data-testid={`antiguedad-${client.id}`}>{getAntiguedad(client)}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(client.status)}`}>
                        {statusLabels[client.status]}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="btn-ghost p-2"
                          onClick={() => handleViewClient(client)} data-testid={`btn-view-${client.id}`}>
                          <History size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" className="btn-ghost p-2"
                          onClick={() => handleEditClient(client)} data-testid={`btn-edit-${client.id}`}>
                          <Edit size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" className="btn-ghost p-2"
                          onClick={() => handleNewCase(client)} data-testid={`btn-case-${client.id}`}>
                          <FileText size={16} />
                        </Button>
                        {isDirection() && (
                          <Button variant="ghost" size="sm" className="btn-ghost p-2 text-[#C25953] hover:text-[#A63D3D] hover:bg-[#FBEAE9]"
                            onClick={() => handleDeleteClient(client)} data-testid={`btn-delete-client-${client.id}`}>
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8E2D7]" data-testid="pagination-controls">
            <p className="text-sm text-[#5C7083]">
              Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredClients.length)} de {filteredClients.length}
            </p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)} data-testid="btn-prev-page"
                className="text-xs px-3">Anterior</Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-[#5C7083]">...</span>
                  ) : (
                    <Button key={p} variant={p === currentPage ? 'default' : 'outline'} size="sm"
                      onClick={() => setCurrentPage(p)} data-testid={`btn-page-${p}`}
                      className={`text-xs w-8 h-8 p-0 ${p === currentPage ? 'bg-[#2B4257] text-white' : ''}`}>
                      {p}
                    </Button>
                  )
                )}
              <Button variant="outline" size="sm" disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)} data-testid="btn-next-page"
                className="text-xs px-3">Siguiente</Button>
            </div>
          </div>
        )}
      </div>

      {/* New/Edit Client Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedClient ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveClient} className="space-y-4" data-testid="client-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="form-label">Nombre *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required data-testid="input-client-name" />
              </div>
              <div>
                <Label className="form-label">Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="input-client-email" />
              </div>
              <div>
                <Label className="form-label">Teléfono</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="input-client-phone" />
              </div>
              <div>
                <Label className="form-label">RUT</Label>
                <Input value={formData.rut} onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                  placeholder="12.345.678-9" data-testid="input-client-rut" />
              </div>
              <div>
                <Label className="form-label">Plan</Label>
                <Select value={formData.current_plan || "none"}
                  onValueChange={(v) => setFormData({ ...formData, current_plan: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="select-client-plan"><SelectValue placeholder="Seleccionar plan" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin plan</SelectItem>
                    {allPlans.map((plan) => <SelectItem key={plan} value={plan}>{plan}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Origen</Label>
                <Select value={formData.origen} onValueChange={(v) => setFormData({ ...formData, origen: v })}>
                  <SelectTrigger data-testid="select-client-origen"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {clientOrigins.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Estado</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="select-client-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="congelado">Congelado</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Inicio Plan (Desde)</Label>
                  <Input type="date" value={formData.fecha_inicio_plan}
                    onChange={(e) => {
                      const startDate = e.target.value;
                      const updates = { fecha_inicio_plan: startDate };
                      // Auto-calculate end date: +12 months for VPos subs
                      if (startDate) {
                        const dt = new Date(startDate + 'T00:00:00');
                        dt.setFullYear(dt.getFullYear() + 1);
                        updates.fecha_fin_plan = dt.toISOString().split('T')[0];
                      }
                      setFormData({ ...formData, ...updates });
                    }}
                    data-testid="input-client-fecha-inicio" />
                  {formData.origen === 'virtualpos' && (
                    <p className="text-[10px] text-[#5C7083] mt-1">* Fecha se carga automaticamente desde VPos</p>
                  )}
                </div>
                <div>
                  <Label className="form-label">Fin Plan (Hasta)</Label>
                  <Input type="date" value={formData.fecha_fin_plan}
                    onChange={(e) => setFormData({ ...formData, fecha_fin_plan: e.target.value })}
                    data-testid="input-client-fecha-fin" />
                  <p className="text-[10px] text-[#5C7083] mt-1">Suscripciones: 12 meses desde inicio</p>
                </div>
              </div>
              <div>
                <Label className="form-label">Teléfono Emergencia</Label>
                <Input value={formData.telefono_emergencia}
                  onChange={(e) => setFormData({ ...formData, telefono_emergencia: e.target.value })}
                  className="form-input" placeholder="+56 9..."
                  data-testid="input-client-tel-emergencia" />
              </div>
              <div className="col-span-2">
                <Label className="form-label">Historial de Lesiones</Label>
                <textarea value={formData.lesiones}
                  onChange={(e) => setFormData({ ...formData, lesiones: e.target.value })}
                  className="form-input min-h-[80px] resize-none w-full"
                  placeholder="Una lesión por línea. Ej:&#10;Lesión rodilla derecha (2024)&#10;Tendinitis hombro izquierdo (2025)"
                  data-testid="input-client-lesiones" />
              </div>
              <div className="col-span-2">
                <Label className="form-label">Observaciones</Label>
                <textarea value={formData.observations}
                  onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                  className="form-input min-h-[60px] resize-none w-full" data-testid="input-client-observations" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowNewDialog(false)} className="btn-secondary">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="btn-save-client">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {selectedClient ? 'Guardar' : 'Crear Cliente'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Ficha del Cliente</DialogTitle></DialogHeader>
          {selectedClient && (
            <div className="space-y-5" data-testid="client-detail">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div><span className="text-[#5C7083]">Nombre:</span> <span className="font-medium ml-1">{selectedClient.name}</span></div>
                <div><span className="text-[#5C7083]">Estado:</span> <span className={`ml-1 status-badge ${getStatusClass(selectedClient.status)}`}>{statusLabels[selectedClient.status]}</span></div>
                <div><span className="text-[#5C7083]">Origen:</span> <span className="ml-1">{getOrigenBadge(selectedClient.origen)}</span></div>
                <div><span className="text-[#5C7083]">Email:</span> <span className="ml-1">{selectedClient.email || '-'}</span></div>
                <div><span className="text-[#5C7083]">Teléfono:</span> <span className="ml-1">{selectedClient.phone || '-'}</span></div>
                <div><span className="text-[#5C7083]">Plan:</span> <span className="ml-1">{selectedClient.current_plan || '-'}</span></div>
                <div><span className="text-[#5C7083]">Inicio Plan:</span> <span className="ml-1">{selectedClient.fecha_inicio_plan || '-'}</span></div>
                <div><span className="text-[#5C7083]">Fin Plan:</span> <span className="ml-1">{selectedClient.fecha_fin_plan || '-'}</span>
                  {(() => { const exp = getPlanExpiryStatus(selectedClient); return exp ? <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full font-medium ${exp.class}`}>{exp.label}</span> : null; })()}
                </div>
                <div><span className="text-[#5C7083]">Tel. Emergencia:</span> <span className="ml-1">{selectedClient.telefono_emergencia || '-'}</span></div>
              </div>
              {/* Historial de Lesiones */}
              {selectedClient.lesiones && (Array.isArray(selectedClient.lesiones) ? selectedClient.lesiones.length > 0 : selectedClient.lesiones) && (
                <div className="p-3 bg-[#FBEAE9]/50 border border-[#C25953]/20 rounded-lg text-sm" data-testid="client-lesiones">
                  <p className="font-medium text-[#C25953] mb-1">Historial de Lesiones</p>
                  {(Array.isArray(selectedClient.lesiones) ? selectedClient.lesiones : [selectedClient.lesiones]).map((l, i) => (
                    <p key={`lesion-${i}-${l?.slice(0,10)}`} className="text-[#5C7083] flex items-start gap-1.5">
                      <span className="text-[#C25953] mt-0.5 flex-shrink-0">&#8226;</span> {l}
                    </p>
                  ))}
                </div>
              )}
              {selectedClient.observations && (
                <div className="p-3 bg-[#F5F0E8] rounded-lg text-sm">
                  <span className="text-[#5C7083]">Observaciones:</span> {selectedClient.observations}
                </div>
              )}
              <div>
                <h4 className="font-semibold text-[#1A2835] mb-3">Casos Operativos</h4>
                <div className="max-h-[250px] overflow-y-auto space-y-2">
                  {clientCases.length === 0 ? (
                    <p className="text-[#5C7083] text-sm py-4 text-center">No hay casos registrados</p>
                  ) : (
                    clientCases.map((c) => (
                      <div key={c.id} className="flex items-center justify-between p-3 bg-[#F5F0E8] rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            c.tipo_accion === 'nuevo_cliente' ? 'bg-green-100 text-green-700' :
                            c.tipo_accion === 'cancelacion' ? 'bg-red-100 text-red-700' :
                            c.tipo_accion === 'congelacion' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{movementTypeLabels[c.tipo_accion] || c.tipo_accion}</span>
                          {c.plan && <span className="text-sm text-[#5C7083]">{c.plan}</span>}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-[#5C7083]">{c.fecha_accion}</p>
                          <p className={`text-xs ${c.status === 'cerrado' ? 'text-green-600' : 'text-amber-600'}`}>
                            {c.status === 'cerrado' ? 'Cerrado' : 'Abierto'}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="btn-secondary">Cerrar</Button>
                <Button className="btn-primary" onClick={() => { setShowDetailDialog(false); handleNewCase(selectedClient); }}>
                  Nuevo Caso Operativo
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Case Dialog (from Client) */}
      <Dialog open={showCaseDialog} onOpenChange={setShowCaseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuevo Caso Operativo</DialogTitle></DialogHeader>
          {selectedClient && (
            <form onSubmit={handleSaveCase} className="space-y-4" data-testid="case-form">
              <div className="p-3 bg-[#F5F0E8] rounded-lg">
                <p className="text-sm text-[#5C7083]">Cliente</p>
                <p className="font-medium">{selectedClient.name}</p>
              </div>
              <div>
                <Label className="form-label">Tipo *</Label>
                <Select value={caseData.tipo_accion || undefined}
                  onValueChange={(v) => setCaseData({ ...caseData, tipo_accion: v })}>
                  <SelectTrigger data-testid="select-case-type"><SelectValue placeholder="Seleccionar tipo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nuevo_cliente">Nuevo Cliente</SelectItem>
                    <SelectItem value="renovacion">Renovación</SelectItem>
                    <SelectItem value="cambio_plan">Cambio de Plan</SelectItem>
                    <SelectItem value="congelacion">Congelación</SelectItem>
                    <SelectItem value="descongelacion">Descongelación</SelectItem>
                    <SelectItem value="cancelacion">Cancelación</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Plan</Label>
                  <Select value={caseData.plan || "none"}
                    onValueChange={(v) => setCaseData({ ...caseData, plan: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="select-case-plan"><SelectValue placeholder="Plan" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-</SelectItem>
                      {allPlans.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">Fecha</Label>
                  <Input type="date" value={caseData.fecha_accion}
                    onChange={(e) => setCaseData({ ...caseData, fecha_accion: e.target.value })}
                    data-testid="input-case-date" />
                </div>
              </div>
              <div>
                <Label className="form-label">Observaciones</Label>
                <textarea value={caseData.observaciones}
                  onChange={(e) => setCaseData({ ...caseData, observaciones: e.target.value })}
                  className="form-input min-h-[60px] resize-none w-full" data-testid="input-case-observations" />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowCaseDialog(false)} className="btn-secondary">Cancelar</Button>
                <Button type="submit" className="btn-primary" disabled={saving || !caseData.tipo_accion} data-testid="btn-save-case">
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Registrar
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clientes;
